import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'

import ActionButton from '@/components/misc/ActionButton'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter } from '@/components/ui/card'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { DATE_FORMATS } from '@/lib/constants'
import { faPencil, faThumbtack, faTrashAlt } from '@/lib/fontAwesomePro/regular'
import { formatDate } from '@/lib/helpers/dateHelpers'
import { buildDebugTooltip } from '@/lib/helpers/helperFunctions'
import { cn } from '@/lib/utils'
import { CommentCollectionItem } from '@/types/responses/comments'

interface Props {
  comment: CommentCollectionItem
  onPin: () => void
  isPinning: boolean
  onEdit: () => void
  onDelete: () => void
  isDeleting: boolean
}

const Comment: React.FC<Props> = ({
  comment,
  onPin,
  isPinning,
  onEdit,
  isDeleting,
  onDelete,
}) => {
  const permissions = comment.permissions

  return (
    <Card
      title={buildDebugTooltip(comment)}
      className={cn(
        'mb-6',
        comment.isPinned ? 'border-primary bg-blue-50 shadow-lg' : '',
      )}
    >
      <CardContent className="whitespace-pre-wrap">
        <header className="mb-4">
          {comment.user && (
            <div className="font-bold text-black">{comment.user.fullName}</div>
          )}

          {comment.createdAt && (
            <div className="xxs text-dim">
              {formatDate(comment.createdAt, DATE_FORMATS.MONTH_DAY_YEAR_TIME)}
            </div>
          )}
        </header>

        {comment.body}
      </CardContent>

      <CardFooter
        className="text-dim flex flex-wrap gap-2 text-xs sm:justify-between"
      >
        <div className="flex shrink-0 gap-2">
          {/* Pin / Unpin */}
          {permissions.togglePinStatus && (
            <Tooltip>
              <TooltipTrigger asChild>
                <ActionButton
                  variant={
                    comment.isPinned ? 'outline-primary' : 'outline-secondary'
                  }
                  className="flex flex-wrap items-center gap-2"
                  onClick={onPin}
                  isProcessing={isPinning}
                  showIsProcessingText={false}
                  showSpinner={false}
                >
                  <FontAwesomeIcon
                    icon={faThumbtack}
                    style={{ transform: 'rotate(40deg)' }}
                  />
                </ActionButton>
              </TooltipTrigger>

              <TooltipContent>
                {comment.isPinned ? 'Unpin' : 'Pin'}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Edit */}
          {permissions.edit && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline-secondary" onClick={onEdit}>
                  <FontAwesomeIcon icon={faPencil} />
                </Button>
              </TooltipTrigger>

              <TooltipContent>Edit</TooltipContent>
            </Tooltip>
          )}

          {/* Delete */}
          {permissions.delete && (
            <Tooltip>
              <TooltipTrigger asChild>
                <ActionButton
                  variant="outline-destructive"
                  onClick={onDelete}
                  isProcessing={isDeleting}
                  showIsProcessingText={false}
                  showSpinner={false}
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                </ActionButton>
              </TooltipTrigger>

              <TooltipContent>Delete</TooltipContent>
            </Tooltip>
          )}
        </div>
      </CardFooter>
    </Card>
  )
}

export default Comment
